import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "next-themes";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, PenTool, Type, FileText } from "lucide-react";
import { formatDateET } from "@/lib/dates";

export default function SignExternalDocumentPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signed, setSigned] = useState(false);
  const [sigMode, setSigMode] = useState("typed");
  const [typedName, setTypedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const { theme, setTheme } = useTheme();
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/sign-document/${token}/pdf`;

  // Force light mode on this public page; restore admin theme on unmount.
  useEffect(() => {
    const prev = theme;
    setTheme("light");
    return () => { if (prev && prev !== "light") setTheme(prev); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) { setError("Missing token"); setLoading(false); return; }
    api.get(`/sign-document/${token}/info`)
      .then(({ data }) => {
        if (data.status === "signed") { setSigned(true); }
        setInfo(data);
      })
      .catch((err) => setError(err.response?.data?.detail || "Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (sigMode === "drawn" && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = canvas.offsetWidth;
      canvas.height = 150;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#18181b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  }, [sigMode]);

  const startDraw = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const stopDraw = () => setDrawing(false);
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSign = async () => {
    let sigData = "";
    let signerName = info?.lead_name || "";
    if (sigMode === "typed") {
      if (!typedName.trim()) { toast.error("Please type your full name"); return; }
      // For typed mode, render the typed name as a small PNG so the PDF page has an image.
      const tmp = document.createElement("canvas");
      tmp.width = 600; tmp.height = 150;
      const ctx = tmp.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, tmp.width, tmp.height);
      ctx.fillStyle = "#18181b"; ctx.font = "italic 48px serif";
      ctx.fillText(typedName, 20, 90);
      sigData = tmp.toDataURL("image/png");
      signerName = typedName.trim();
    } else {
      sigData = canvasRef.current?.toDataURL("image/png") || "";
      if (!sigData || sigData.length < 1000) { toast.error("Please draw your signature"); return; }
    }
    setSubmitting(true);
    try {
      await api.post(`/sign-document/${token}`, {
        signature_type: sigMode,
        signature_data: sigData,
        signer_name: signerName,
      });
      setSigned(true);
      toast.success("Document signed!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signing failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-gray-400">Loading document...</p></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <Card className="w-full max-w-md text-center bg-white border-gray-200">
        <CardContent className="p-8 space-y-3">
          <p className="text-lg font-semibold text-gray-900">Link Unavailable</p>
          <p className="text-sm text-gray-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );
  if (signed) return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <Card className="w-full max-w-md text-center bg-white border-gray-200">
        <CardContent className="p-8 space-y-4">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
          <h2 className="text-2xl font-bold text-gray-900">Document Signed!</h2>
          <p className="text-gray-500">Thank you. The signed copy has been sent to the team.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-white py-6 px-4" data-testid="sign-external-document-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Document Signature Request</h1>
          <p className="text-gray-500 text-sm mt-1">Hi {info?.lead_name || ""}, please review and sign below.</p>
        </div>

        <Card className="bg-white border-gray-200">
          <CardContent className="p-4 sm:p-6 space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FileText size={16} className="text-gray-500" />
              <span className="font-medium truncate">{info?.filename}</span>
              {info?.size && <span className="text-xs text-gray-400">· {(info.size / 1024).toFixed(1)} KB</span>}
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <iframe
                title="Document"
                src={pdfUrl}
                className="w-full"
                style={{ height: "70vh", minHeight: "420px" }}
                data-testid="sign-document-pdf"
              />
            </div>
            <p className="text-xs text-gray-500">
              Can't see the PDF? <a href={pdfUrl} className="text-primary underline" target="_blank" rel="noreferrer">Open in a new tab</a>
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200">
          <CardContent className="p-6 bg-white space-y-4">
            <div className="flex gap-2">
              <Button variant={sigMode === "typed" ? "default" : "outline"} size="sm" onClick={() => setSigMode("typed")} data-testid="sig-typed-btn">
                <Type size={14} /> Type Name
              </Button>
              <Button variant={sigMode === "drawn" ? "default" : "outline"} size="sm" onClick={() => setSigMode("drawn")} data-testid="sig-draw-btn">
                <PenTool size={14} /> Draw Signature
              </Button>
            </div>

            {sigMode === "typed" ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Type your full legal name</label>
                <Input
                  data-testid="sig-typed-input"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Full Name"
                  className="text-lg font-serif italic"
                />
                {typedName && (
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-2xl font-serif italic text-gray-800">{typedName}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDateET(new Date())}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Draw your signature below</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    data-testid="sig-canvas"
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={stopDraw}
                    onMouseLeave={stopDraw}
                    onTouchStart={startDraw}
                    onTouchMove={draw}
                    onTouchEnd={stopDraw}
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={clearCanvas} data-testid="sig-clear">Clear</Button>
              </div>
            )}

            <Button data-testid="sign-document-btn" className="w-full" onClick={handleSign} disabled={submitting}>
              {submitting ? "Signing..." : "Sign Document"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
